"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Input,
  P2,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tag,
  Text,
  TextLabel,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@focal/ui-kit";

import {
  Client,
  ContactPhoneType,
  CONTACT_PHONE_TYPE_LABELS,
  PhoneNumber,
} from "@/types";
import { formatPhoneNumber, removeNonNumeric } from "@/lib";
import { useCrm } from "@/components/pages/Settings/integrations/hooks";
import { useClientContacts } from "@/components/pages/Clients/components/hooks";
import { ContactListHandle } from "./types";

/** Maximum number of phone numbers a client may carry (enforced client-side). */
const MAX_PHONES = 5;

const PRIMARY_PHONE_TOOLTIP =
  "This is the primary phone number that will be used by Focal for identifying this client on incoming and outgoing calls.";

/** A staged edit to a phone row: formatted number + selected type. */
interface PhoneDraft {
  value: string;
  type: ContactPhoneType;
}

interface ClientPhoneListProps {
  client: Client;
  /**
   * When true, the list is display-only: rows render as plain text with no
   * inputs or actions. Used on the client detail surface where modification
   * lives exclusively in the edit modal.
   */
  readOnly?: boolean;
}

/**
 * Renders a client's full set of phone numbers, each with a type
 * (Work / Home / Mobile) dropdown.
 *
 * In editable mode every row is an always-editable input + type select. Typing
 * or changing the type only updates local draft state — value edits are NOT
 * persisted until the parent modal calls
 * {@link ContactListHandle.flushPendingEdits} (on its Save button). A per-row
 * 3-dot menu exposes the immediate actions (make-primary, delete) and an inline
 * "Add phone" affordance persists new rows immediately.
 *
 * Numbers are displayed via {@link formatPhoneNumber}; edits store the raw
 * 10-digit value (via {@link removeNonNumeric}) so the formatting behaviour
 * matches the existing `ClientModal` / `PhoneInput`.
 *
 * All mutations are wired through {@link useClientContacts}, which invalidates
 * the relevant query keys so the details header, this list, and the clients
 * table all reflect backend behaviour (primary-promotion-on-delete and the
 * `client.phone` mirror writeback).
 *
 * When the client is CRM-synced and contact sync is enabled, value edits,
 * type changes, add, and delete are disabled (those belong in the CRM,
 * mirroring the guards in `ClientModal`). Promoting an existing number to
 * primary is a benign local re-ordering and stays available.
 */
export const ClientPhoneList = forwardRef<
  ContactListHandle,
  ClientPhoneListProps
>(({ client, readOnly = false }, ref) => {
  const { isContactSyncEnabled } = useCrm();
  const { addPhone, updatePhone, deletePhone, setPrimaryPhone } =
    useClientContacts(client.id);

  // Disable mutating actions for CRM-synced clients when contact sync is on:
  // those edits must happen in the CRM, mirroring ClientModal's guards.
  const isSyncLocked = !!client.crmContact?.crmId && isContactSyncEnabled;

  const phones: PhoneNumber[] = useMemo(
    () => client.phoneNumbers ?? [],
    [client.phoneNumbers],
  );

  // Preserve a stable display order while editing. Promoting a row to primary
  // re-sorts the API response (primary first), which would make the row jump to
  // the top. We render rows in the order they first appeared (new rows append at
  // the end), so only the "Primary" label moves. The read-only profile display
  // keeps the API's primary-first order, which is fine there.
  const orderRef = useRef<string[]>([]);
  const orderedPhones = useMemo(() => {
    const byId = new Map(phones.map((phone) => [phone.id, phone]));
    const order = orderRef.current.filter((id) => byId.has(id));
    const known = new Set(order);
    for (const phone of phones) if (!known.has(phone.id)) order.push(phone.id);
    orderRef.current = order;
    return order.map((id) => byId.get(id)!);
  }, [phones]);

  // Staged value edits, keyed by phone id. A row is "dirty" when its draft
  // number or type differs from the persisted value. Drafts persist across the
  // refetches triggered by immediate actions (delete / make-primary / add) and
  // are only cleared once flushed or once the row disappears.
  const [drafts, setDrafts] = useState<Record<string, PhoneDraft>>({});

  // Drop drafts for rows that no longer exist (e.g. after a delete refetch).
  useEffect(() => {
    setDrafts((prev) => {
      const ids = new Set(phones.map((phone) => phone.id));
      const next: Record<string, PhoneDraft> = {};
      let changed = false;
      for (const [id, draft] of Object.entries(prev)) {
        if (ids.has(id)) next[id] = draft;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [phones]);

  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [addType, setAddType] = useState<ContactPhoneType>(
    ContactPhoneType.HOME,
  );
  const [addError, setAddError] = useState<string | null>(null);

  // Validate that a raw value resolves to exactly 10 digits, mirroring the
  // 10-digit limit the modal's PhoneInput enforces.
  const validatePhone = (value: string): string | null => {
    const digits = removeNonNumeric(value);
    if (digits.length !== 10) return "Enter a valid 10-digit phone number";
    return null;
  };

  const valueFor = (phone: PhoneNumber) =>
    drafts[phone.id]?.value ?? formatPhoneNumber(phone.number);

  const typeFor = (phone: PhoneNumber) => drafts[phone.id]?.type ?? phone.type;

  const isDirty = (phone: PhoneNumber) => {
    const draft = drafts[phone.id];
    if (!draft) return false;
    return (
      removeNonNumeric(draft.value) !== phone.number ||
      draft.type !== phone.type
    );
  };

  const rowError = (phone: PhoneNumber): string | null =>
    isDirty(phone) ? validatePhone(valueFor(phone)) : null;

  const setDraft = (phone: PhoneNumber, patch: Partial<PhoneDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [phone.id]: {
        value: patch.value ?? valueFor(phone),
        type: patch.type ?? typeFor(phone),
      },
    }));

  const startAdding = () => {
    setIsAdding(true);
    setAddValue("");
    setAddType(ContactPhoneType.HOME);
    setAddError(null);
  };

  const cancelAdding = useCallback(() => {
    setIsAdding(false);
    setAddValue("");
    setAddType(ContactPhoneType.HOME);
    setAddError(null);
  }, []);

  const saveAdd = () => {
    const error = validatePhone(addValue);
    if (error) {
      setAddError(error);
      return;
    }
    addPhone.mutate(
      {
        number: removeNonNumeric(addValue),
        type: addType,
        primary: phones.length === 0,
      },
      { onSuccess: cancelAdding },
    );
  };

  // Persist all dirty, valid rows. Skips invalid rows (their inline error is
  // already shown). Clears each draft once its edit succeeds; a failed edit
  // keeps its draft so the user can correct it. Also commits a pending "Add
  // phone" row the user typed but didn't confirm with the check, so hitting the
  // modal's Save no longer silently drops it. Returns the number of edits/adds
  // that failed so the parent can skip a misleading success toast.
  const flushPendingEdits = useCallback(async () => {
    const edits = phones.filter((phone) => {
      const draft = drafts[phone.id];
      if (!draft) return false;
      const digits = removeNonNumeric(draft.value);
      const changed = digits !== phone.number || draft.type !== phone.type;
      return changed && digits.length === 10;
    });

    const results = await Promise.allSettled(
      edits.map(async (phone) => {
        const draft = drafts[phone.id];
        await updatePhone.mutateAsync({
          phoneId: phone.id,
          number: removeNonNumeric(draft.value),
          type: draft.type,
        });
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[phone.id];
          return next;
        });
      }),
    );

    let failures = results.filter(
      (result) => result.status === "rejected",
    ).length;

    // Commit an in-progress add row (typed but not confirmed via the check).
    if (isAdding && removeNonNumeric(addValue).length > 0) {
      const error = validatePhone(addValue);
      if (error) {
        setAddError(error);
        failures += 1;
      } else {
        try {
          await addPhone.mutateAsync({
            number: removeNonNumeric(addValue),
            type: addType,
            primary: phones.length === 0,
          });
          cancelAdding();
        } catch {
          // addPhone's onError already surfaced a toast; keep the row open.
          failures += 1;
        }
      }
    }

    return failures;
  }, [phones, drafts, updatePhone, isAdding, addValue, addType, addPhone, cancelAdding]);

  useImperativeHandle(ref, () => ({ flushPendingEdits }), [flushPendingEdits]);

  const renderActionButton = (
    button: React.ReactNode,
    disabledTooltip: string,
  ) => {
    if (!isSyncLocked) return button;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex">{button}</div>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  };

  const syncTooltip =
    "Contact sync is on. Manage this client's phone numbers in your CRM instead.";

  const renderTypeSelect = (
    value: ContactPhoneType,
    onValueChange: (value: ContactPhoneType) => void,
    disabled = false,
  ) => (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => onValueChange(next as ContactPhoneType)}
    >
      <SelectTrigger
        className="w-[110px] shrink-0"
        data-cy="client-phone-type"
        aria-label="Phone type"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(ContactPhoneType).map((type) => (
          <SelectItem key={type} value={type}>
            {CONTACT_PHONE_TYPE_LABELS[type]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderPrimaryTag = () => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">
          <Tag>Primary</Tag>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{PRIMARY_PHONE_TOOLTIP}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex flex-1 flex-col gap-2 max-w-[360px] min-w-[250px]">
      <TextLabel>Phone</TextLabel>

      <div className="flex flex-col gap-2">
        {(readOnly ? phones : orderedPhones).map((phone) => {
          if (readOnly) {
            return (
              <div
                key={phone.id}
                data-cy="client-phone-row"
                className="flex items-center gap-2"
              >
                <P2 textColor="secondary" className="min-w-0 break-all">
                  {formatPhoneNumber(phone.number)}
                </P2>
                <Tag className="shrink-0">
                  {CONTACT_PHONE_TYPE_LABELS[phone.type]}
                </Tag>
                {phone.primary && renderPrimaryTag()}
              </div>
            );
          }

          const error = rowError(phone);

          return (
            <div
              key={phone.id}
              data-cy="client-phone-row"
              className="flex items-center gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Input
                    type="tel"
                    value={valueFor(phone)}
                    data-cy="client-phone-input"
                    disabled={isSyncLocked}
                    onChange={(event) =>
                      setDraft(phone, {
                        value: formatPhoneNumber(
                          removeNonNumeric(event.target.value).slice(0, 10),
                        ),
                      })
                    }
                    placeholder="(555) 555-5555"
                  />
                  {renderTypeSelect(
                    typeFor(phone),
                    (next) => setDraft(phone, { type: next }),
                    isSyncLocked,
                  )}
                </div>
                {error && (
                  <Text variant="p3" textColor="error">
                    {error}
                  </Text>
                )}
              </div>

              {phone.primary ? (
                renderPrimaryTag()
              ) : (
                // Reserve the primary-pill slot so every input stops at the
                // same right edge instead of growing into the empty space.
                <span aria-hidden className="invisible inline-flex shrink-0">
                  <Tag>Primary</Tag>
                </span>
              )}

              {/* Fixed-width action slot: keeps every input's right edge aligned
                  across rows, including the wider "Add phone" row (check +
                  cancel). The slot always reserves its width, so a sync-locked
                  primary row (which renders no menu) still lines up. */}
              <div className="flex w-14 shrink-0 items-center justify-end">
                {/* Make-primary is allowed even when sync-locked; delete is not. */}
                {(!phone.primary || !isSyncLocked) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="iconSm"
                        variant="ghostSecondary"
                        type="button"
                        aria-label="Phone options"
                        data-cy="client-phone-menu"
                      >
                        <Icon name="MoreVertIcon" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!phone.primary && (
                        <DropdownMenuItem
                          data-cy="client-phone-make-primary"
                          disabled={setPrimaryPhone.isPending}
                          onClick={() => setPrimaryPhone.mutate(phone.id)}
                        >
                          Make primary
                        </DropdownMenuItem>
                      )}
                      {!isSyncLocked && (
                        <DropdownMenuItem
                          data-cy="client-phone-delete"
                          disabled={deletePhone.isPending}
                          onClick={() => deletePhone.mutate(phone.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          );
        })}

        {!readOnly && isAdding && (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <Input
                  type="tel"
                  value={addValue}
                  data-cy="client-phone-input"
                  onChange={(event) =>
                    setAddValue(
                      formatPhoneNumber(
                        removeNonNumeric(event.target.value).slice(0, 10),
                      ),
                    )
                  }
                  placeholder="(555) 555-5555"
                />
                {renderTypeSelect(addType, setAddType)}
              </div>
              {addError && (
                <Text variant="p3" textColor="error">
                  {addError}
                </Text>
              )}
            </div>
            {/* Mirror the saved rows' trailing layout (reserved primary slot +
                fixed action slot) so the add-row input lines up with them. */}
            <span aria-hidden className="invisible inline-flex shrink-0">
              <Tag>Primary</Tag>
            </span>
            <div className="flex w-14 shrink-0 items-center justify-end gap-1">
              <Button
                size="iconSm"
                variant="ghost"
                type="button"
                aria-label="Save new phone"
                disabled={addPhone.isPending}
                onClick={saveAdd}
              >
                <Icon name="CheckIcon" />
              </Button>
              <Button
                size="iconSm"
                variant="ghostSecondary"
                type="button"
                aria-label="Cancel adding phone"
                onClick={cancelAdding}
              >
                <Icon name="CloseIcon" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {!readOnly && !isAdding && phones.length >= MAX_PHONES && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex w-fit">
              <Button
                size="sm"
                variant="link"
                type="button"
                data-cy="client-phone-add"
                disabled
                iconLeft={<Icon name="AddIcon" />}
              >
                Add phone
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            You can add up to {MAX_PHONES} phone numbers.
          </TooltipContent>
        </Tooltip>
      )}

      {!readOnly &&
        !isAdding &&
        phones.length < MAX_PHONES &&
        renderActionButton(
          <Button
            size="sm"
            variant="link"
            type="button"
            className="w-fit"
            data-cy="client-phone-add"
            disabled={isSyncLocked}
            iconLeft={<Icon name="AddIcon" />}
            onClick={startAdding}
          >
            Add phone
          </Button>,
          syncTooltip,
        )}
    </div>
  );
});

ClientPhoneList.displayName = "ClientPhoneList";
